import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
    Box, Typography, Card, CardContent, Grid,
    CircularProgress, Alert, ToggleButton, ToggleButtonGroup, Button,
} from "@mui/material";
import { PieChart } from "@mui/x-charts/PieChart";
import { EmojiEvents, TrendingUp, TrendingDown, Percent } from "@mui/icons-material";
import { WB_SX, WB_UI } from "../../../ui/ux/uiTokens";
import CRMStatCard from "../common/CRMStatCard";
import axiosInstance from "../../../api/axiosInstance";
import base_url from "../../../constants";
import useNotification from "../../common/Notification";
import {
    DEAL_STATUS_META,
    OTHERS_COLOR,
    formatWindowDate,
    formatShare,
    isUnknownCategory,
    sortByValueDesc,
    withChartColors,
    groupTopN,
    normalizeBySourceResponse,
    normalizeDealStatusCounts,
    dealStatusWinRate,
} from "../common/crmAnalyticsUtils";

const MONTH_OPTIONS = [
    { value: 1, label: "1 month" },
    { value: 2, label: "2 months" },
    { value: 3, label: "3 months" },
];

const PRODUCT_VISIBLE_COUNT = 8;

const DistributionBar = ({ label, value, total, max, color, muted = false }) => {
    const width = max > 0 ? (value / max) * 100 : 0;
    return (
        <Box sx={{ opacity: muted ? 0.78 : 1 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 1, mb: 0.5 }}>
                <Typography
                    variant="body2"
                    title={label}
                    sx={{
                        fontWeight: 600,
                        color: muted ? "text.secondary" : "text.primary",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    }}
                >
                    {label}
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 700, color: "text.primary", flexShrink: 0 }}>
                    {value}
                    <Box component="span" sx={{ color: "text.secondary", fontWeight: 600 }}>
                        {` · ${formatShare(value, total)}`}
                    </Box>
                </Typography>
            </Box>
            <Box sx={{ height: 8, borderRadius: 999, backgroundColor: "rgba(0,0,0,0.06)", overflow: "hidden" }}>
                <Box
                    sx={{
                        width: `${width}%`,
                        height: "100%",
                        borderRadius: 999,
                        backgroundColor: color || "#1d4ed8",
                        transition: "width 0.25s ease",
                    }}
                />
            </Box>
        </Box>
    );
};

const CRMAnalytics = () => {
    const _notify = useNotification();
    const notifyRef = useRef(_notify);
    notifyRef.current = _notify;

    const [months, setMonths] = useState(1);
    const [dealStatus, setDealStatus] = useState(null);
    const [statusLoading, setStatusLoading] = useState(true);
    const [statusError, setStatusError] = useState(null);

    const [leadsBySource, setLeadsBySource] = useState([]);
    const [contactsTotal, setContactsTotal] = useState(0);
    const [dealsByProductService, setDealsByProductService] = useState([]);
    const [chartsLoading, setChartsLoading] = useState(true);
    const [chartsError, setChartsError] = useState(null);
    const [showAllProducts, setShowAllProducts] = useState(false);

    const fetchDealStatus = useCallback(async (monthWindow) => {
        setStatusLoading(true);
        setStatusError(null);
        try {
            const res = await axiosInstance.get(`${base_url}/crm/dashboard/deal-status-counts`, {
                params: { months: monthWindow },
            });
            setDealStatus(normalizeDealStatusCounts(res.data));
        } catch (err) {
            const msg = err?.response?.data?.message || "Failed to load deal status counts";
            setStatusError(msg);
            setDealStatus(null);
            notifyRef.current(msg, "error");
        } finally {
            setStatusLoading(false);
        }
    }, []);

    const fetchCharts = useCallback(async () => {
        setChartsLoading(true);
        setChartsError(null);
        try {
            const [statsRes, bySourceRes] = await Promise.all([
                axiosInstance.get(`${base_url}/crm/dashboard`),
                axiosInstance.get(`${base_url}/crm/contacts/by-source`).catch(() => ({ data: {} })),
            ]);
            const stats = statsRes.data?.data ?? statsRes.data;
            setDealsByProductService(
                Array.isArray(stats?.deals_by_product_service) ? stats.deals_by_product_service : []
            );
            const { leadsBySource: sourceRows, total } = normalizeBySourceResponse(bySourceRes.data);
            setLeadsBySource(sourceRows);
            setContactsTotal(total);
        } catch (err) {
            const msg = err?.response?.data?.message || "Failed to load analytics charts";
            setChartsError(msg);
            notifyRef.current(msg, "error");
        } finally {
            setChartsLoading(false);
        }
    }, []);

    useEffect(() => { fetchDealStatus(months); }, [months, fetchDealStatus]);
    useEffect(() => { fetchCharts(); }, [fetchCharts]);

    const winRate = dealStatusWinRate(dealStatus || {});
    const hasDealCounts = (dealStatus?.deals_total || 0) > 0;
    const statusBars = dealStatus ? [
        { key: "open", value: dealStatus.deals_open, ...DEAL_STATUS_META.open },
        { key: "won",  value: dealStatus.deals_won,  ...DEAL_STATUS_META.won },
        { key: "lost", value: dealStatus.deals_lost, ...DEAL_STATUS_META.lost },
    ] : [];
    const maxStatusCount = statusBars.reduce((max, row) => Math.max(max, row.value), 0);

    const statusPieData = useMemo(() => {
        if (!dealStatus) return [];
        return [
            { id: "open", label: DEAL_STATUS_META.open.label, value: dealStatus.deals_open, color: DEAL_STATUS_META.open.color },
            { id: "won",  label: DEAL_STATUS_META.won.label,  value: dealStatus.deals_won,  color: DEAL_STATUS_META.won.color },
            { id: "lost", label: DEAL_STATUS_META.lost.label, value: dealStatus.deals_lost, color: DEAL_STATUS_META.lost.color },
        ].filter((item) => item.value > 0);
    }, [dealStatus]);

    const rankedSources = useMemo(
        () => withChartColors(sortByValueDesc(
            leadsBySource.map((row, i) => ({
                id: `${row.source}-${i}`,
                label: row.source,
                value: Number(row.count) || 0,
            }))
        )),
        [leadsBySource]
    );
    const { pieItems: sourcePieItems } = useMemo(
        () => groupTopN(rankedSources, 5),
        [rankedSources]
    );
    const sourcePieData = useMemo(
        () => sourcePieItems.map((row) => ({
            id: row.id,
            label: row.label,
            value: row.value,
            color: row.color,
        })),
        [sourcePieItems]
    );
    const topSource = rankedSources[0] || null;

    const productRows = useMemo(
        () => sortByValueDesc(
            dealsByProductService.map((row, i) => ({
                id: `${row?.name || "product"}-${i}`,
                label: row?.name || "Unknown product/service",
                value: Number(row?.deals_count) || 0,
            }))
        ),
        [dealsByProductService]
    );
    const unknownProducts = productRows.filter((row) => isUnknownCategory(row.label));
    const namedProducts = productRows.filter((row) => !isUnknownCategory(row.label));
    const unknownDealCount = unknownProducts.reduce((sum, row) => sum + row.value, 0);
    const productTotal = productRows.reduce((sum, row) => sum + row.value, 0);
    const namedTotal = namedProducts.reduce((sum, row) => sum + row.value, 0);
    const visibleNamedProducts = showAllProducts
        ? namedProducts
        : namedProducts.slice(0, PRODUCT_VISIBLE_COUNT);
    const hiddenNamedProducts = showAllProducts ? [] : namedProducts.slice(PRODUCT_VISIBLE_COUNT);
    const hiddenNamedCount = hiddenNamedProducts.reduce((sum, row) => sum + row.value, 0);

    const windowLabel = dealStatus?.from && dealStatus?.to
        ? `${formatWindowDate(dealStatus.from)} – ${formatWindowDate(dealStatus.to)}`
        : `Last ${months} month${months > 1 ? "s" : ""}`;

    return (
        <Box sx={WB_SX.page}>
            <Box sx={{ mb: 3 }}>
                <Typography variant="h5" sx={WB_SX.pageTitle}>
                    CRM Analytics
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Deal outcomes, lead sources, and product mix
                </Typography>
            </Box>

            <Card elevation={0} sx={{ ...WB_SX.card, mb: WB_UI.spacing.sectionGap }}>
                <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: { xs: "stretch", sm: "center" },
                            flexDirection: { xs: "column", sm: "row" },
                            gap: 1.5,
                            mb: 2,
                        }}
                    >
                        <Box>
                            <Typography variant="subtitle1" sx={WB_SX.sectionTitle}>
                                Deal status
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Open, won, and lost deals created in the selected window
                                {dealStatus ? ` · ${windowLabel}` : ""}
                            </Typography>
                        </Box>
                        <ToggleButtonGroup
                            exclusive
                            size="small"
                            value={months}
                            onChange={(_, value) => { if (value) setMonths(Number(value)); }}
                            sx={{
                                alignSelf: { xs: "flex-start", sm: "center" },
                                "& .MuiToggleButton-root": {
                                    textTransform: "none",
                                    px: 1.5,
                                    fontWeight: 600,
                                    fontSize: 12,
                                },
                            }}
                        >
                            {MONTH_OPTIONS.map((opt) => (
                                <ToggleButton key={opt.value} value={opt.value}>
                                    {opt.label}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    </Box>

                    {statusLoading && !dealStatus ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                            <CircularProgress size={28} />
                        </Box>
                    ) : statusError && !dealStatus ? (
                        <Alert severity="error">{statusError}</Alert>
                    ) : (
                        <>
                            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2.5, position: "relative" }}>
                                {statusLoading && (
                                    <Box
                                        sx={{
                                            position: "absolute",
                                            inset: 0,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            bgcolor: "rgba(255,255,255,0.55)",
                                            zIndex: 1,
                                            borderRadius: 1,
                                        }}
                                    >
                                        <CircularProgress size={22} />
                                    </Box>
                                )}
                                <CRMStatCard
                                    label="Open"
                                    value={dealStatus?.deals_open ?? "—"}
                                    sub={`of ${dealStatus?.deals_total ?? 0} deals`}
                                    accent={DEAL_STATUS_META.open.color}
                                    icon={<TrendingUp fontSize="small" />}
                                />
                                <CRMStatCard
                                    label="Won"
                                    value={dealStatus?.deals_won ?? "—"}
                                    sub={winRate != null ? `${winRate}% win rate` : "No closed deals"}
                                    accent={DEAL_STATUS_META.won.color}
                                    icon={<EmojiEvents fontSize="small" />}
                                />
                                <CRMStatCard
                                    label="Lost"
                                    value={dealStatus?.deals_lost ?? "—"}
                                    sub={`${dealStatus?.deals_won ?? 0} won in window`}
                                    accent={DEAL_STATUS_META.lost.color}
                                    icon={<TrendingDown fontSize="small" />}
                                />
                                <CRMStatCard
                                    label="Win rate"
                                    value={winRate != null ? `${winRate}%` : "—"}
                                    sub="Won ÷ closed deals"
                                    accent="#7c3aed"
                                    icon={<Percent fontSize="small" />}
                                />
                            </Box>

                            {!hasDealCounts ? (
                                <Typography variant="body2" color="text.disabled" sx={{ py: 4, textAlign: "center" }}>
                                    No deals created in this period
                                </Typography>
                            ) : (
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", display: "block", mb: 1 }}>
                                            Mix of deal outcomes
                                        </Typography>
                                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                                            <PieChart
                                                height={280}
                                                width={360}
                                                series={[{
                                                    data: statusPieData,
                                                    innerRadius: 52,
                                                    outerRadius: 100,
                                                    paddingAngle: 2,
                                                    arcLabel: (item) => String(item.value),
                                                    arcLabelMinAngle: 20,
                                                }]}
                                                slotProps={{
                                                    legend: {
                                                        direction: "column",
                                                        position: { vertical: "middle", horizontal: "right" },
                                                        labelStyle: { fontSize: 12 },
                                                    },
                                                }}
                                            />
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", display: "block", mb: 1 }}>
                                            Deal counts by status
                                        </Typography>
                                        <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", gap: 2, minHeight: 240, py: 1 }}>
                                            {statusBars.map((seg) => {
                                                const ratio = maxStatusCount > 0
                                                    ? Math.max(seg.value > 0 ? 8 : 0, (seg.value / maxStatusCount) * 100)
                                                    : 0;
                                                const share = dealStatus.deals_total > 0
                                                    ? Math.round((seg.value / dealStatus.deals_total) * 100)
                                                    : 0;
                                                return (
                                                    <Box key={seg.key}>
                                                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                                                            <Typography variant="body2" sx={{ fontWeight: 700, color: seg.color }}>
                                                                {seg.label}
                                                            </Typography>
                                                            <Typography variant="caption" sx={{ fontWeight: 700, color: "text.primary" }}>
                                                                {seg.value}
                                                                <Box component="span" sx={{ color: "text.secondary", fontWeight: 600 }}>
                                                                    {` · ${share}%`}
                                                                </Box>
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{ height: 12, borderRadius: 999, backgroundColor: "rgba(0,0,0,0.06)", overflow: "hidden" }}>
                                                            <Box
                                                                sx={{
                                                                    width: `${ratio}%`,
                                                                    height: "100%",
                                                                    borderRadius: 999,
                                                                    backgroundColor: seg.color,
                                                                    transition: "width 0.25s ease",
                                                                }}
                                                            />
                                                        </Box>
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    </Grid>
                                </Grid>
                            )}
                        </>
                    )}
                </CardContent>
            </Card>

            {chartsError && !leadsBySource.length && !dealsByProductService.length ? (
                <Alert severity="error">{chartsError}</Alert>
            ) : chartsLoading && !leadsBySource.length && !dealsByProductService.length ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                    <CircularProgress size={28} />
                </Box>
            ) : (
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Card elevation={0} sx={WB_SX.card}>
                            <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1, mb: 2, flexWrap: "wrap" }}>
                                    <Box>
                                        <Typography variant="subtitle1" sx={{ ...WB_SX.sectionTitle, mb: 0.5 }}>
                                            Leads by source
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                                            Contact distribution across lead sources
                                            {contactsTotal > 0 ? ` · ${contactsTotal} total contact${contactsTotal !== 1 ? "s" : ""}` : ""}
                                            {topSource ? ` · Top: ${topSource.label} (${formatShare(topSource.value, contactsTotal)})` : ""}
                                        </Typography>
                                    </Box>
                                </Box>
                                {leadsBySource.length === 0 ? (
                                    <Typography variant="body2" color="text.disabled" sx={{ py: 3, textAlign: "center" }}>
                                        No contact data yet
                                    </Typography>
                                ) : (
                                    <Grid container spacing={2} alignItems="center">
                                        <Grid item xs={12} md={5}>
                                            <Box sx={{ position: "relative", width: 280, height: 280, mx: "auto" }}>
                                                <PieChart
                                                    height={280}
                                                    width={280}
                                                    margin={{ top: 8, bottom: 8, left: 8, right: 8 }}
                                                    series={[{
                                                        data: sourcePieData,
                                                        innerRadius: 62,
                                                        outerRadius: 108,
                                                        paddingAngle: 2,
                                                        cx: 140,
                                                        cy: 140,
                                                    }]}
                                                    slotProps={{ legend: { hidden: true } }}
                                                />
                                                <Box
                                                    sx={{
                                                        position: "absolute",
                                                        inset: 0,
                                                        display: "grid",
                                                        placeItems: "center",
                                                        pointerEvents: "none",
                                                    }}
                                                >
                                                    <Box sx={{ textAlign: "center" }}>
                                                        <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1 }}>
                                                            {contactsTotal}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            contacts
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Box>
                                            <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 1, mt: 1 }}>
                                                {sourcePieItems.map((item) => (
                                                    <Box key={item.id} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                                        <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: item.color, flexShrink: 0 }} />
                                                        <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>
                                                            {item.label}
                                                            {item.isOthers && item.children?.length ? ` (${item.children.length})` : ""}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} md={7}>
                                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                                                {rankedSources.map((row) => (
                                                    <DistributionBar
                                                        key={row.id}
                                                        label={row.label}
                                                        value={row.value}
                                                        total={contactsTotal}
                                                        max={contactsTotal}
                                                        color={row.color}
                                                    />
                                                ))}
                                            </Box>
                                        </Grid>
                                    </Grid>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12}>
                        <Card elevation={0} sx={WB_SX.card}>
                            <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
                                <Typography variant="subtitle1" sx={{ ...WB_SX.sectionTitle, mb: 0.5 }}>
                                    Deals by product / service
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
                                    Share of deals that are linked to a product/service
                                    {namedTotal > 0 ? ` · ${namedTotal} linked` : ""}
                                    {productTotal > 0 ? ` · ${productTotal} total` : ""}
                                </Typography>
                                {dealsByProductService.length === 0 ? (
                                    <Typography variant="body2" color="text.disabled" sx={{ py: 3, textAlign: "center" }}>
                                        No product/service deal data yet
                                    </Typography>
                                ) : (
                                    <>
                                        {unknownDealCount > 0 && (
                                            <Alert severity="warning" variant="outlined" sx={{ mb: 2, py: 0.5 }}>
                                                {unknownDealCount} deal{unknownDealCount !== 1 ? "s" : ""} ({formatShare(unknownDealCount, productTotal)}) have no product/service linked
                                            </Alert>
                                        )}
                                        {namedProducts.length === 0 ? (
                                            <Typography variant="body2" color="text.disabled" sx={{ py: 2, textAlign: "center" }}>
                                                No named product/service deals yet
                                            </Typography>
                                        ) : (
                                            <>
                                                <Grid container spacing={2}>
                                                    {visibleNamedProducts.map((row) => (
                                                        <Grid item xs={12} md={6} key={row.id}>
                                                            <DistributionBar
                                                                label={row.label}
                                                                value={row.value}
                                                                total={namedTotal}
                                                                max={namedTotal}
                                                                color="#1d4ed8"
                                                            />
                                                        </Grid>
                                                    ))}
                                                </Grid>
                                                {hiddenNamedProducts.length > 0 && (
                                                    <Box sx={{ mt: 2 }}>
                                                        <DistributionBar
                                                            label={`Others (${hiddenNamedProducts.length} products)`}
                                                            value={hiddenNamedCount}
                                                            total={namedTotal}
                                                            max={namedTotal}
                                                            color={OTHERS_COLOR}
                                                            muted
                                                        />
                                                    </Box>
                                                )}
                                                {namedProducts.length > PRODUCT_VISIBLE_COUNT && (
                                                    <Button
                                                        size="small"
                                                        onClick={() => setShowAllProducts((open) => !open)}
                                                        sx={{ mt: 1.5, textTransform: "none", fontWeight: 600 }}
                                                    >
                                                        {showAllProducts
                                                            ? "Show top products"
                                                            : `Show all ${namedProducts.length} products`}
                                                    </Button>
                                                )}
                                                {namedTotal > 0 && (
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
                                                        {namedTotal} deal{namedTotal !== 1 ? "s" : ""} linked to a product/service
                                                    </Typography>
                                                )}
                                            </>
                                        )}
                                    </>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}
        </Box>
    );
};

export default CRMAnalytics;
